import { useState, useCallback, useEffect, useRef } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';
import type { DocumentNode } from 'graphql';
import type { UseIssuesInput } from '@atlassian/jira-business-common/src/common/types/query-input.tsx';
import { getVariables } from '@atlassian/jira-business-common/src/common/utils/get-variables/index.tsx';
import { useJqlContext } from '@atlassian/jira-business-entity-common/src/controllers/jql-context/index.tsx';
import { isAbortError } from '@atlassian/jira-business-error-handling/src/utils/abort-error/index.tsx';
import type { UpdaterReturn } from '@atlassian/jira-business-list-handlers/src/controllers/pagination/types.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { GenericVariables } from '../../common/types';
import type { AGGCompliantAPI } from '../pagination/types';
import { getAGGCompliantData } from '../pagination/utils';
import { useRequestQueue } from '../request-queue';
import { useCacheActionsReducer } from './utils/cache-actions-reducer';

type Params = { issueId: number; parentId?: number | string };

export type UseViewUpdaterInput = {
	projectKey?: string;
	fieldIds: string[];
	isHidingDoneItems?: boolean;
	quickFiltersJql?: string | null;
	isParentFieldVisible?: boolean;
	isDevSummaryFieldVisible?: boolean;
};

/**
 * Loads individual items, dealing with the cache updates
 * This service keeps the list updated based on any changes to the list given the applied filters/sorting.
 * - If an item is changed and doesn't meet the filter criteria anymore, this is the code that will remove it from the screen
 * - the same goes for the opposite, if an item is changed and now meets the criteria, this code will add it to the screen
 *
 * For example
 * So if `BusinessRealtime` (Jira realtime service) receive new item that’s not in the list, but matching the filter (e.g. someone else adding a new issue)
 * The code here will help to include the data inside apollo cache
 */
export const useViewUpdater = <
	TIssueNode extends {
		readonly issueId: number;
	},
	TItemDetails_items extends {
		readonly edges:
			| ReadonlyArray<{ node: TIssueNode | null } | null | undefined>
			| null
			| undefined;
	},
	TItemDetails extends AGGCompliantAPI<TItemDetails_items>,
>({
	input,
	query,
	actions,
	onError,
	onAppendItemsError,
}: {
	input: UseViewUpdaterInput;
	query: DocumentNode;
	actions: {
		addToCache?: (items: TIssueNode[]) => void;
		removeFromCache?: (items: TIssueNode[]) => void;
		handleFilteredUpdate?: (issueIds: number[]) => void;
	}[];
	onError: (arg1: ApolloError) => void;
	onAppendItemsError: (arg1: ApolloError) => void;
}): UpdaterReturn => {
	const jqlContext = useJqlContext();
	const cloudId = useCloudId();

	// Keep track of updates in flight so we can validate them against the current list of issues.
	// Useful when filters are enabled and items are changed in a way that do not match the filter anymore.
	const [updateInFlightList, setUpdateInFlightList] = useState<UseIssuesInput[]>([]);

	const [cacheActionIssues, dispatch] = useCacheActionsReducer<TIssueNode>();

	// getIssuesByIdVariablesRef is for using the variables in the useCallback for onComplete,
	// otherwise it errors because it is referenced before it's declared and is undefined
	const getIssuesByIdVariablesRef = useRef<GenericVariables>();
	const getIssuesByIdOnCompletedRef = useRef<((result: TItemDetails) => void) | null>(null);

	// Separate query to handle issue removal
	const [fetchIssueToRemove, { loading: fetchIssueToRemoveLoading }] = useLazyQuery<
		TItemDetails,
		GenericVariables
	>(query, {
		fetchPolicy: 'network-only',
		errorPolicy: 'all',
		onCompleted: useCallback(
			(result: TItemDetails) => {
				const items = getAGGCompliantData<TItemDetails, TIssueNode>(result, 'list');
				const validNodes = items?.edges?.map((edge) => edge?.node).filter(Boolean);
				if (validNodes && validNodes.length > 0) {
					dispatch({ type: 'REMOVE_FROM_CACHE', issues: validNodes });
				}
			},
			[dispatch],
		),
		onError: onAppendItemsError,
	});

	// Separated query to update individual items so the other query can deal with pagination in the right sequence
	const [getIssuesById, { variables: getIssuesByIdVariables, loading: getIssuesByIdLoading }] =
		useLazyQuery<TItemDetails, GenericVariables>(query, {
			fetchPolicy: 'no-cache',
			errorPolicy: 'all',
			onError: useCallback(
				(error: ApolloError) => {
					if (isAbortError(error.networkError)) {
						return;
					}

					onError(error);
				},
				[onError],
			),
			onCompleted: useCallback(
				(result: TItemDetails) => getIssuesByIdOnCompletedRef.current?.(result),
				[],
			),
		});

	if (getIssuesByIdVariablesRef.current !== getIssuesByIdVariables) {
		getIssuesByIdVariablesRef.current = getIssuesByIdVariables;
	}

	// Separated query to append newly created items so we know it needs to be added to the top of the list.
	// Adding to the top to mimic backend behaviour but this might need to change when we introduce sorting.
	const [appendIssuesById, { loading: appendIssuesByIdLoading }] = useLazyQuery<
		TItemDetails,
		GenericVariables
	>(query, {
		fetchPolicy: 'network-only',
		errorPolicy: 'all',
		onCompleted: useCallback(
			(result: TItemDetails) => {
				const items = getAGGCompliantData<TItemDetails, TIssueNode>(result, 'list', onError);
				const validNodes = items?.edges?.map((edge) => edge?.node).filter(Boolean);
				if (validNodes && validNodes.length > 0) {
					dispatch({ type: 'ADD_TO_CACHE', issues: validNodes });
				}
			},
			[dispatch, onError],
		),
		onError: onAppendItemsError,
	});

	const { addRequestToQueue, addRequestToAbortableQueue } = useRequestQueue({
		isRequestLoading: fetchIssueToRemoveLoading || getIssuesByIdLoading || appendIssuesByIdLoading,
	});

	const removeIssueById = useCallback(
		(issueIdsToRemove: number[]) => {
			addRequestToQueue(() =>
				fetchIssueToRemove({
					variables: {
						...getVariables({
							jqlContext,
							issueIds: issueIdsToRemove,
							fieldIds: input.fieldIds,
							isHidingDoneItems: input.isHidingDoneItems,
							isParentFieldVisible: input.isParentFieldVisible,
							isDevSummaryFieldVisible: input.isDevSummaryFieldVisible,
							projectIdOrKey: ff('list-uses-new-jql-parent-project-directive_v5gfs')
								? input.projectKey ?? undefined
								: undefined,
							// we don't want to include any quickFiltersJql on purpose
							// because we actually want to get the data of the issue that is not suppose to match the current quickFiltersJql
							// e.g. consider this scenario
							// - if the current quickFiltersJql is filtering DONE item
							// - and the issueId that we need to remove is because user moving it to DONE
							// - if we include the quickFiltersJql in here, the BE will NOT return the issue data because it's already DONE -> so we can't filter it in FE
							// - so in this case we just want the data of the issue
							quickFiltersJql: null,
						}),
						cloudId,
					},
				}),
			);
		},
		[
			addRequestToQueue,
			fetchIssueToRemove,
			jqlContext,
			input.fieldIds,
			input.isHidingDoneItems,
			input.isParentFieldVisible,
			input.isDevSummaryFieldVisible,
			input.projectKey,
			cloudId,
		],
	);

	const removeIssue = useCallback(
		({ issueId, parentId }: Params) => {
			// @ts-expect-error ts(2322) - we can't instantiate an issue of the generic type, as we can't know the whole issue
			// However, the removeFromCache function called with this issue only needs this part of the interface
			const issueNode: TIssueNode = {
				issueId,
				fields:
					parentId != null
						? [
								{
									fieldId: 'parent',
									type: PARENT_TYPE,
									parentIssue: {
										issueId: parentId,
									},
								},
							]
						: [],
			};
			dispatch({
				type: 'REMOVE_FROM_CACHE',
				issues: [issueNode],
			});
		},
		[dispatch],
	);

	// Filters are handled by JQL (not client side). The update function respects the filters so a BLANK update
	// means the item findIssueById didn't match the JQL filter and SHOULD be removed.
	const handleFilterMismatch = useCallback(
		(variables: GenericVariables, result: TItemDetails) => {
			if (!variables.jql) {
				return;
			}

			if (!result || !result.jira) {
				// this is a case where the request failed, we don't want to remove the issue from the list in this case
				return;
			}

			const updateIndex = updateInFlightList.findIndex(
				(updateInput) => getVariables(updateInput).jql === variables.jql,
			);

			if (updateIndex === -1) {
				return;
			}

			const currentUpdate = updateInFlightList[updateIndex];
			// In slow connections, users might change filters after an update was requested but before it can be applied.
			// We should not process those!
			const isUsingSameFilter = currentUpdate.quickFiltersJql === input.quickFiltersJql;
			const issueIds = [...(currentUpdate.issueIds || [])];

			const items = getAGGCompliantData<TItemDetails, TIssueNode>(result, 'list');
			const validNodes = items?.edges?.map((edge) => edge?.node).filter(Boolean);
			if (isUsingSameFilter) {
				const issueIdsToRemove: Array<number> = [];

				issueIds.forEach((issueId, idIndex) => {
					// item exists in the list but not in the filtered results - remove it!
					const item = validNodes?.find((node) => node?.issueId === issueId);
					if (item == null) {
						issueIdsToRemove.push(issueId);
					}

					issueIds.splice(idIndex, 0);
					updateInFlightList[updateIndex] = { ...currentUpdate, issueIds };
				});

				if (issueIdsToRemove.length > 0) {
					removeIssueById(issueIdsToRemove);
				}
			}

			setUpdateInFlightList((prevUpdateInFlightList) => {
				prevUpdateInFlightList.splice(updateIndex, 1);
				return prevUpdateInFlightList;
			});

			// if item returns in filtered results it should be added to the cache. The method will dedup and sort by rank.
			if (validNodes && validNodes.length > 0) {
				dispatch({ type: 'ADD_TO_CACHE', issues: validNodes });
			}

			// if item has been filtered out we may still want to do something with it
			const filteredIssueIds: number[] = issueIds.filter(
				(issueId) => !(validNodes ?? []).find((node) => node?.issueId === issueId),
			);
			if (filteredIssueIds.length > 0) {
				dispatch({ type: 'HANDLE_FILTERED_UPDATE', issues: filteredIssueIds });
			}
		},
		[updateInFlightList, input, removeIssueById, dispatch],
	);

	getIssuesByIdOnCompletedRef.current = (result: TItemDetails) =>
		handleFilterMismatch(getIssuesByIdVariables, result);

	// Interact with the cache in a useEffect to avoid doing it faster than the group items query data is updated
	useEffect(() => {
		if (
			cacheActionIssues.addToCache.length === 0 &&
			cacheActionIssues.removeFromCache.length === 0 &&
			cacheActionIssues.handleFilteredUpdate.length === 0
		) {
			return;
		}

		/* eslint-disable @typescript-eslint/consistent-type-assertions */
		(
			Object.entries(cacheActionIssues) as Array<
				[keyof typeof cacheActionIssues, (typeof cacheActionIssues)[keyof typeof cacheActionIssues]]
			>
		)
			/* eslint-enable @typescript-eslint/consistent-type-assertions */
			.forEach(([actionName, issues]) => {
				if (Array.isArray(issues) && issues.length > 0) {
					actions.forEach((actionEntry) => {
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
						actionEntry?.[actionName]?.(issues as any);
					});
				}
			});

		dispatch({ type: 'CLEAR' });
	}, [cacheActionIssues, actions, dispatch]);

	return {
		onIssueUpdated: (issueIds: number[]) => {
			// we want to fetch this issueIds
			// but at the same time we pass the `quickFiltersJql`
			// so if in the end result, we don't get back the `issueIds`, we know that the updated issue is not valid anymore in the current `quickFiltersJql`
			// e.g.
			// - current quickFiltersJql is NOT showing DONE item
			// - then there is an update to an ISSUE-X that changes the status
			// - we then fetch updated data of ISSUE_X based on current quickFiltersJql
			// - if the ISSUE-X is returned in the result -> it means ISSUE-X still matches current quickFiltersJql
			// - otherwise it is not, so it will be handled in handleFilterMismatch to be removed from view
			const updateInput = {
				jqlContext,
				issueIds,
				fieldIds: input.fieldIds,
				quickFiltersJql: input.quickFiltersJql,
				isHidingDoneItems: input.isHidingDoneItems,
				isParentFieldVisible: input.isParentFieldVisible,
				isDevSummaryFieldVisible: input.isDevSummaryFieldVisible,
				projectIdOrKey: ff('list-uses-new-jql-parent-project-directive_v5gfs')
					? input.projectKey ?? undefined
					: undefined,
			};
			setUpdateInFlightList((prevUpdateInFlightList) => [...prevUpdateInFlightList, updateInput]);

			addRequestToAbortableQueue<TItemDetails, GenericVariables>(issueIds, getIssuesById, {
				variables: { ...getVariables(updateInput), cloudId },
			});
		},
		onIssueCreated: (issueIds: number[]) => {
			addRequestToQueue(() =>
				appendIssuesById({
					variables: {
						...getVariables({
							jqlContext,
							issueIds,
							fieldIds: input.fieldIds,
							quickFiltersJql: input.quickFiltersJql,
							isHidingDoneItems: input.isHidingDoneItems,
							isParentFieldVisible: input.isParentFieldVisible,
							isDevSummaryFieldVisible: input.isDevSummaryFieldVisible,
							projectIdOrKey: ff('list-uses-new-jql-parent-project-directive_v5gfs')
								? input.projectKey ?? undefined
								: undefined,
						}),
						cloudId,
					},
				}),
			);
		},
		onIssueRemoved: (params: Params) => removeIssue(params),
	};
};
